<script setup lang="ts">
import axios from 'axios'
import { Mail, MoreHorizontal, Pencil, Store, Trash2 } from 'lucide-vue-next'

import type { User } from './UserModal.vue'

const { hasPermissionBySlug } = useAccess()
const { t } = useI18n()
const { user, languages } = useLocalStorage()

const userId = user.value.id
const isLoaded = ref(false)
const table = ref({
  pagination: {
    currentPage: 1,
    lastPage: 1,
    total: 1
  },
  rows: [],
  dropdown: {
    label: '',
    values: [],
    translate: false,
    userId: 0,
    userRole: ''
  },
  columns: {
    name: {
      label: 'pages.settings.usersPage.tables.thead.name'
    },
    email: {
      label: 'pages.settings.usersPage.tables.thead.email'
    },
    roleTemplate: {
      label: 'pages.settings.usersPage.tables.thead.role'
    },
    stores: {
      label: 'pages.settings.usersPage.tables.thead.store'
    },
    lastLoginDate: {
      label: 'pages.settings.usersPage.tables.thead.lastLoginDate'
    },
    languageId: {
      label: 'pages.settings.usersPage.tables.thead.language'
    }
  }
})

const translatedColumns = computed(() =>
  Object.fromEntries(
    Object.entries(table.value.columns).map(([key, col]) => [key, { ...col, label: t((col as any).label) }])
  )
)

const handleOptionClick = (callbackFn: string, payload: any) => {
  if (callbackFn === 'selectUser:delete') emit('selectUser:delete', payload)
  else if (callbackFn === 'selectUser:update') emit('selectUser:update', payload)
  else if (callbackFn === 'selectUser:resendInvitation') emit('selectUser:resendInvitation', payload, true)
}
const rolesList = ref([])
const currentUserRole = ref(null)

const emit = defineEmits<{
  (e: 'selectUser:update', user: User): void
  (e: 'selectUser:delete', user: User): void
  (e: 'selectUser:resendInvitation', user: User, resendMail: boolean): void
}>()

const getRoles = async () => {
  const { data } = await axios.get('role-templates')
  rolesList.value = []

  data.data.forEach((role) => {
    rolesList.value.push({
      id: role.roleTemplate.id,
      text: role.roleTemplate.name
    })
  })
}

const getUsers = async () => {
  isLoaded.value = false
  try {
    const { data } = await axios.get(`/users`, {
      params: {
        pageSize: 15,
        page: table.value.pagination.currentPage,
        merchantId: user.value.merchant.id
      }
    })

    const rows = []
    const users = data.data

    if (hasPermissions.value) {
      Object.assign(table.value.columns, {
        options: {
          label: ''
        }
      })
    }

    let currentUserId = user.value.id

    users.forEach((row) => {
      let options = []
      const userLanguage = languages.value?.find((lang) => lang.id === row.languageId)

      let data: any = {
        name: row.name,
        email: row.email,
        roleTemplate: rolesList.value.filter((role) => role.id === row.role_template_id)[0],
        stores: row.stores,
        lastLoginDate: {
          hasLoggedIn: row.lastLoginDate !== null,
          text: row.lastLoginDate !== null ? new Date(row.lastLoginDate).toLocaleString('fr') : t('global.words.never')
        },
        languageId: {
          value: userLanguage?.id,
          image: userLanguage?.flag
        }
      }

      if (!userLanguage) {
        data.languageId = t('pages.settings.usersPage.errors.language')
      }
      if (hasPermissions.value) {
        if (hasPermissionBySlug('permission.update')) {
          options.push({
            icon: Pencil,
            label: t('global.words.modify'),
            callbackFn: 'selectUser:update'
          })
        }

        if (hasPermissionBySlug('permission.delete') && row.id !== currentUserId) {
          options.push({
            icon: Trash2,
            label: t('global.words.delete'),
            callbackFn: 'selectUser:delete',
            variant: 'destructive'
          })
        }

        if (hasPermissionBySlug('permission.create') || hasPermissionBySlug('permission.update')) {
          options.push({
            icon: Mail,
            label: t('pages.settings.usersPage.crud.resend'),
            callbackFn: 'selectUser:resendInvitation'
          })
        }

        Object.assign(data, { options: options })
      }

      rows.push({
        id: row.id,
        data: data
      })
    })

    table.value.pagination = {
      currentPage: data.meta.current_page,
      lastPage: data.meta.last_page,
      total: data.meta.total
    }

    table.value.rows = rows

    table.value.dropdown = {
      label: 'ui.dropdownStore.selectStores',
      values: rolesList.value,
      translate: false,
      userId: userId,
      userRole: currentUserRole.value
    }
    isLoaded.value = true
  } finally {
    isLoaded.value = true
  }
}

const changePage = (page) => {
  table.value.pagination.currentPage = page
  getUsers()
}

const hasPermissions = computed(() => {
  return (
    hasPermissionBySlug('permission.create') ||
    hasPermissionBySlug('permission.update') ||
    hasPermissionBySlug('permission.delete')
  )
})

onMounted(async () => {
  try {
    await getRoles()
    await getUsers()
  } finally {
    isLoaded.value = true
  }
})
</script>

<template>
  <div v-if="isLoaded" class="pb-6 sm:pb-12">
    <AppTable
      :columns="translatedColumns"
      :rows="table.rows"
      :pagination="table.pagination"
      @update:change-page="changePage"
    >
      <template #td-roleTemplate="{ data }">
        {{ data.roleTemplate?.text ?? '' }}
      </template>
      <template #td-stores="{ data }">
        <span v-if="data.stores.length === 1">{{ data.stores[0].name }}</span>
        <BaseChip v-else :text="t('pages.settings.usersPage.stores', { count: data.stores.length })">
          <template #leading>
            <Store />
          </template>
        </BaseChip>
      </template>
      <template #td-lastLoginDate="{ data }">
        <BaseChip :color="data.lastLoginDate.hasLoggedIn ? 'green' : 'red'" :text="data.lastLoginDate.text" />
      </template>
      <template #td-languageId="{ data }">
        <img v-if="data.languageId?.image" class="h-5" :src="data.languageId.image" />
        <span v-else>{{ data.languageId }}</span>
      </template>
      <template #td-options="{ id, data }">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <button
              type="button"
              class="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground-secondary hover:bg-accent focus:outline-hidden"
            >
              <MoreHorizontal class="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              v-for="option in data.options"
              :key="option.label"
              :variant="option.variant"
              @click="handleOptionClick(option.callbackFn, { id, ...data })"
            >
              <component :is="option.icon" />
              {{ option.label }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </template>
    </AppTable>
  </div>
  <TheLoadingPage v-else page="UsersListPage" />
</template>
